import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { ensureNotNil } from "client/Misc/utils";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { SharePointEvent, publish } from "client_legacy/Documents/SharePoint/documentEvents";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { DocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import { EnabledView } from "client_legacy/Documents/SharePoint/DocumentsViewsPane/types";
import {
	MoveAcrossDataStoresModel,
	SharePointCrossStoreItemModel,
	move
} from "client/Api/Documents/CrossStoreOperations/crossStoreActions";
import {
	moveItems,
	MoveDocumentsModel,
	DocumentMovingModel
} from "client/Api/Documents/SharePoint/sharePointAction";
import {
	ThingsToMove,
	moveNotConfiguredMessage,
	Destination,
	SharePointThingsToMove,
	convertToCrossStoreDestination,
	SharePointDestination
} from "./common";
import {
	MoveResult,
	MoveFailedItem,
	DocumentStoreType
} from "client/Api/Documents/common";
import { HttpRequestError } from "client/Api/httpClient";
import { isCancelledError } from "client_legacy/ndaw/cancellation";

class SharePointMoveImplementer {

	public getEnabledView(
		thingsToMove: ThingsToMove
	): EnabledView {
		const { allowCrossStoreOperations } = documentsConfiguration();

		switch (thingsToMove.storeKind) {
			case DocumentStoreType.SharePoint:
				if (allowCrossStoreOperations) {
					return {
						kind: "all"
					};
				}

				return {
					kind: "single",
					viewId: thingsToMove.source
						.folder
						.viewId,
					reason: moveNotConfiguredMessage
				};

			case DocumentStoreType.WebDav:
				if (allowCrossStoreOperations) {
					return {
						kind: "all"
					};
				}
				return {
					kind: "none",
					reason: moveNotConfiguredMessage
				};
			default:
				throw new UnreachableCaseError(thingsToMove);
		}
	}

	public move(
		thingsToMove: SharePointThingsToMove,
		destination: Destination,
		signal: AbortSignal
	): Promise<MoveResult> {
		const { allowCrossStoreOperations } = documentsConfiguration();
		switch (destination.storeKind) {
			case DocumentStoreType.SharePoint:
				if (
					thingsToMove.source
						.folder
						.viewId === destination.target
							.viewId
				) {
					return this.moveInCurrentView(
						thingsToMove,
						destination,
						signal
					);
				}

				if (allowCrossStoreOperations) {
					return this.moveInTheBackground(
						thingsToMove,
						destination,
						signal
					);
				}

				throw new Error("Cross site is not configured");

			case DocumentStoreType.WebDav:
				if (allowCrossStoreOperations) {
					return this.moveInTheBackground(
						thingsToMove,
						destination,
						signal
					);
				}
				throw new Error("Does not support moving to WebDav");

			default:
				throw new UnreachableCaseError(destination);
		}
	}

	private async moveInCurrentView(
		thingsToMove: SharePointThingsToMove,
		destination: SharePointDestination,
		signal: AbortSignal
	): Promise<MoveResult> {
		const { location, folderId } = destination.target;
		const documents: readonly DocumentItemViewModel[] = thingsToMove.documents;

		const data: MoveDocumentsModel = {
			location: convertToLocationModel(location),
			newParentFolderId: folderId,
			documents: documents.map(item => ({
				id: item.id,
				type: item.itemType
			}))
		};

		try {
			await moveItems(data, signal);

			const deletedDocuments: readonly DocumentItemViewModel[] = documents.filter((x) => x.parentFolderId !== folderId);

			publish(SharePointEvent.Deleted, deletedDocuments);
			return { kind: "success" };

		} catch (error) {
			if (isCancelledError(error)) {
				throw error;
			}

			if (error instanceof HttpRequestError && error.statusCode === 400) {

				const response: MoveOnSameViewPartiallyFailed = error.readJson<MoveOnSameViewPartiallyFailed>();
				const deletedDocumentsIds: readonly number[] = response.deletedDocuments.map(x => x.id);

				publish(SharePointEvent.Deleted, documents.filter(
					x => deletedDocumentsIds.includes(x.id)
				));

				const map = new Map<number, DocumentItemViewModel>();
				documents.forEach((item) => map.set(item.id, item));

				const failedItems: readonly MoveFailedItem[] = response.problemDocuments
					.map((x) => ({
						name: ensureNotNil(map.get(x.documentId))
							.name,
						reason: x.errorMessage
					}));

				return { kind: "failed", failedItems };
			}

			return { kind: "failed", failedItems: documents.map((x) => ({ name: x.name })) };
		}
	}

	private async moveInTheBackground(
		thingsToMove: SharePointThingsToMove,
		destination: Destination,
		signal: AbortSignal
	): Promise<MoveResult> {
		const documents: readonly DocumentItemViewModel[] = thingsToMove.documents;
		const itemsToMove: SharePointCrossStoreItemModel[] = documents.map(it => ({
			id: it.id,
			location: convertToLocationModel(it.location)
		}));

		const data: MoveAcrossDataStoresModel = {
			items: {
				storeType: DocumentStoreType.SharePoint,
				sharePointItems: itemsToMove
			},
			destination: convertToCrossStoreDestination(destination)
		};

		try {
			await move(data, signal);
			return { kind: "scheduled" };

		} catch (e) {
			if (isCancelledError(e)) {
				throw e;
			}

			return { kind: "failed", failedItems: documents.map((x) => ({ name: x.name })) };
		}
	}
}

interface ProblemDocument {
	documentId: number;
	errorMessage: string;
}

interface MoveOnSameViewPartiallyFailed {
	problemDocuments: ProblemDocument[];
	deletedDocuments: DocumentMovingModel[];
}

export {
	SharePointMoveImplementer
};
